import { inject, Injectable, signal } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { NGXLogger } from "ngx-logger";
import {
  catchError,
  first,
  Observable,
  switchMap,
  tap,
  throwError,
} from "rxjs";
import { HttpError } from "../models/error.model";
import { Token } from "../models/token.model";
import { AuthService } from "../services/auth.service";
import { CacheService } from "../services/core/cache.service";
import { LogStore } from "./log.store";

const LOG_TAG = "[AUTH-STORE] ";
@Injectable({
  providedIn: "root",
})
export class AuthStore {
  private logger: NGXLogger = inject(NGXLogger);
  private logStore: LogStore = inject(LogStore);
  private authService: AuthService = inject(AuthService);
  private cacheService: CacheService = inject(CacheService);
  private cookieService: CookieService = inject(CookieService);

  // Define signals for the store state
  private readonly logged = signal<boolean>(null);
  private readonly authLoading = signal<boolean>(false);
  private readonly authError = signal<string | null>(null);

  // Getter signals for external access
  readonly logged$ = this.logged.asReadonly();

  // Getter status signals for external access
  readonly authLoading$ = this.authLoading.asReadonly();
  readonly authError$ = this.authError.asReadonly();

  public get adminBehalf() {
    return this.cookieService.get("admin_on_behalf");
  }

  public get token() {
    if (this.adminBehalf) {
      return this.cookieService.get("admin_token");
    } else {
      return localStorage.getItem("token");
    }
  }

  public get refreshToken() {
    return localStorage.getItem("refresh_token");
  }

  public get needAuth(): boolean {
    return (!this.refreshToken || this.adminBehalf) && !this.token
      ? true
      : this._needAuth;
  }

  private _needAuth: boolean;

  public constructor() {
    // const refresh_token = this.refreshToken;
    // console.log("INIT AUTH FROM REFRESH TOKEN", refresh_token);
    // if (refresh_token) {
    //   this.refreshConnection();
    // }
    // effect(
    //   () => {
    //     const logged = this.logged();
    //     if (logged === false) {
    //     }
    //   },
    //   { allowSignalWrites: true }
    // );
  }

  public async login(email: string, password: string, remember: boolean) {
    this.logger.info("login...");

    await this._clearLogged();

    this.authLoading.set(true);
    this.authService.loginForStore(email, password).subscribe({
      next: (token: Token) => {
        this._setLogged(email, token, remember);
        this.authLoading.set(false);
      },
      error: (err) => {
        this.authLoading.set(false);
        if (err instanceof HttpError) {
          this.logger.warn(`error logging in : ${err.error_code} `);
          this.authError.set(err.error_code);
        } else {
          this.logger.warn(`error logging in : ${err} `);
          this.authError.set(`${err}`);
        }
      },
    });
  }

  public refreshConnection(): Observable<any> {
    this.logger.info("refresh connection...");
    if (this.adminBehalf) {
      this.logger.info("need auth in admin mode, clear data and reload page");
      return this.cacheService.clearAll().pipe(
        first(),
        tap(async () => {
          await this._clearLogged();
          location.reload();
        }),
        switchMap(() => {
          throw new Error("exit_admin");
        })
      );
    } else if (!this.refreshToken) {
      this.logger.info("need auth no refresh token, clear data");
      return this.cacheService.clearAll().pipe(
        first(),
        tap(async () => {
          await this._clearLogged(true, true);
        }),
        switchMap(() => {
          throw new Error("session_expired");
        })
      );
    } else {
      this.logger.info("need auth with refresh token, try refresh session");
      this.authLoading.set(true);
      return this.authService.refreshLoginForStore(this.refreshToken).pipe(
        catchError(async (err: HttpError) => {
          if (err.http_status == 401) {
            this.logger.info("refresh token expired => clear data");
          } else {
            this.logger.info(
              `failed to refresh session due to unknown error : ${JSON.stringify(
                err
              )}`
            );
          }
          await this._clearLogged(true, true);
          return throwError(() => new Error("session_expired"));
        }),
        tap((token: Token) => {
          this._setRefreshed(token);
          this.authLoading.set(false);
        })
      );
    }
  }

  public async logout() {
    this.logger.info("logout...");
    await this._clearLogged(true);
  }

  public updateLogged(logged: boolean): void {
    if (this.logged() !== logged) {
      this.logged.set(logged);
    }
  }

  // ---- PRIVATE METHODS -------

  private async _clearLogged(
    logout: boolean = false,
    expired: boolean = false
  ) {
    if (logout && !expired) {
      await this.logStore.flush();
    }

    this._needAuth = true;

    // clear cookies
    document.cookie = "admin_on_behalf=;Max-Age=0;domain=hydrao.com;path=/";
    document.cookie = "admin_token=;Max-Age=0;domain=hydrao.com;path=/";
    this.cookieService.delete("date_selector_pulse_ack");

    // clear local storage
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("login");

    // clear datas in other stores

    if (logout) {
      this.updateLogged(false);
    }
  }

  private _setLogged(login: string, token: Token, remember: boolean) {
    localStorage.setItem("login", login);
    localStorage.setItem("token", token.access_token);
    if (remember) {
      localStorage.setItem("refresh_token", token.refresh_token);
      this.logger.info(`Logged with '${login}' and remember checked`);
    } else {
      this.logger.info(`Logged with '${login}' without remember`);
    }

    this._needAuth = false;
    this.updateLogged(true);
  }

  private _setRefreshed(token: Token) {
    localStorage.setItem("token", token.access_token);
    this._needAuth = false;
  }
}
